import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Icon } from '../components/Icon.tsx';
import { gsrApi, type GsrType, type GsrItemInput } from '../features/gsr/api.ts';
import { useAuthStore } from '../store/auth.ts';
import { t } from '../lib/strings.ts';

interface ItemRow extends Omit<GsrItemInput, 'qtyRequested'> {
  _key: number;
  // Empty until the user types, so the field shows its placeholder (not a default 1).
  qtyRequested: number | undefined;
}

let keyCounter = 0;
function makeRow(partial: Partial<Omit<ItemRow, '_key'>> = {}): ItemRow {
  return {
    _key: ++keyCounter,
    particulars: '',
    qtyRequested: undefined,
    unit: '',
    requiredDate: undefined,
    remarks: '',
    ...partial,
  };
}

export function GsrNew(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);

  const [gsrNumber, setGsrNumber] = useState('');
  const [title, setTitle] = useState('');
  const [type, setType] = useState<GsrType>('GOODS');
  const [items, setItems] = useState<ItemRow[]>([makeRow()]);
  // Sign-off people (org accounts). Requested-by defaults to the creator; the
  // approver is a required selection.
  const [requestedById, setRequestedById] = useState<string>(me?.id ?? '');
  const [approvedById, setApprovedById] = useState<string>('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  const { data: userOptions = [] } = useQuery({
    queryKey: ['user-options'],
    queryFn: () => gsrApi.userOptions(),
  });

  function addRow() {
    setItems((rows) => [...rows, makeRow()]);
  }

  function removeRow(key: number) {
    setItems((rows) => rows.filter((r) => r._key !== key));
  }

  function updateRow(key: number, patch: Partial<Omit<ItemRow, '_key'>>) {
    setItems((rows) => rows.map((r) => r._key === key ? { ...r, ...patch } : r));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    if (!gsrNumber.trim()) {
      setSubmitError(t.gsr.errorNumberRequired);
      return;
    }
    if (!title.trim()) {
      setSubmitError(t.gsr.errorTitleRequired);
      return;
    }
    if (!approvedById) {
      setSubmitError(t.gsr.errorApproverRequired);
      return;
    }
    const validItems = items.filter(
      (i) => i.particulars.trim() && i.unit.trim() && !!i.qtyRequested && i.qtyRequested > 0
    );
    if (validItems.length === 0) {
      setSubmitError(t.gsr.errorNoItems);
      return;
    }

    setIsPending(true);
    try {
      const created = await gsrApi.create({
        gsrNumber: gsrNumber.trim(),
        title: title.trim(),
        type,
        // Omit requestedBy when it's still the creator — the server defaults it.
        requestedById: requestedById && requestedById !== me?.id ? requestedById : undefined,
        approvedById,
        items: validItems.map(({ particulars, qtyRequested, unit, requiredDate, remarks }) => ({
          particulars: particulars.trim(),
          qtyRequested: qtyRequested!,
          unit: unit.trim(),
          requiredDate: requiredDate || undefined,
          remarks: remarks?.trim() || undefined,
        })),
      });
      void qc.invalidateQueries({ queryKey: ['gsrs'] });
      void navigate({ to: '/gsr/$gsrId', params: { gsrId: created.id } });
    } catch (err) {
      const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setSubmitError(code === 'gsr_number_taken' ? t.gsr.errorNumberTaken : t.gsr.errorFailed);
    } finally {
      setIsPending(false);
    }
  }

  return (
    <div className="page space-y-6">
      <header>
        <h1 className="font-serif text-3xl tracking-tight">{t.gsr.createTitle}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t.gsr.createSubtitle}
        </p>
      </header>

      <form className="space-y-6" onSubmit={(e) => { void handleSubmit(e); }}>
        {/* Top row — type, GSR number, and the free-text details (thafseel). */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* Type toggle — shown first */}
          <section className="card-paper space-y-1 p-4">
            <span className="field-label">{t.gsr.typeLabel}</span>
            <div className="mt-1 flex gap-2">
              {(['GOODS', 'SERVICE'] as GsrType[]).map((opt) => (
                <label
                  key={opt}
                  className={[
                    'flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors',
                    type === opt
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border text-muted-foreground hover:bg-muted/60',
                  ].join(' ')}
                >
                  <input
                    type="radio"
                    name="gsrType"
                    value={opt}
                    checked={type === opt}
                    onChange={() => setType(opt)}
                    className="sr-only"
                  />
                  {opt === 'GOODS' ? t.gsr.typeGoods2 : t.gsr.typeService2}
                </label>
              ))}
            </div>
          </section>

          {/* GSR number — manually entered, required */}
          <section className="card-paper space-y-1 p-4">
            <label className="field-label" htmlFor="gsrNumber">{t.gsr.numberLabel}</label>
            <input
              id="gsrNumber"
              required
              className="input"
              placeholder={t.gsr.numberPlaceholder}
              value={gsrNumber}
              onChange={(e) => setGsrNumber(e.target.value)}
            />
          </section>

          {/* Thafseel (details) — becomes the GSR / RFQ title */}
          <section className="card-paper space-y-1 p-4 sm:col-span-2 lg:col-span-1">
            <label className="field-label" htmlFor="gsrTitle">{t.gsr.thafseelLabel}</label>
            <input
              id="gsrTitle"
              required
              className="input"
              placeholder={t.gsr.thafseelPlaceholder}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </section>
        </div>

        {/* Items editor */}
        <section className="space-y-2">
          <div className="flex items-baseline justify-between">
            <h2 className="text-base font-semibold text-ink">{t.gsr.itemsSectionTitle}</h2>
            <span className="text-xs text-muted-foreground num">{items.length}</span>
          </div>

          <div className="card-paper overflow-hidden">
            {/* Column headers (desktop) */}
            <div className="hidden grid-cols-[2fr_4rem_6rem_8rem_1fr_2.5rem] gap-3 border-b border-border bg-muted/40 px-4 py-2.5 sm:grid">
              <span className="status-label text-muted-foreground text-start">{t.gsr.colParticulars}</span>
              <span className="status-label text-muted-foreground text-start">{t.gsr.colQtyRequested}</span>
              <span className="status-label text-muted-foreground text-start">{t.gsr.colUnit}</span>
              <span className="status-label text-muted-foreground text-start">{t.gsr.colRequiredDate}</span>
              <span className="status-label text-muted-foreground text-start">{t.gsr.colRemarks}</span>
              <span aria-hidden />
            </div>

            <div className="divide-y divide-border">
              {items.map((row) => (
                <div
                  key={row._key}
                  className="grid grid-cols-1 items-center gap-2 p-3 sm:grid-cols-[2fr_4rem_6rem_8rem_1fr_2.5rem] sm:gap-3 sm:px-4"
                >
                  <input
                    required
                    className="input"
                    placeholder={t.gsr.placeholderParticulars}
                    aria-label={t.gsr.ariaParticulars}
                    value={row.particulars}
                    onChange={(e) => updateRow(row._key, { particulars: e.target.value })}
                  />
                  <input
                    required
                    type="number"
                    min={1}
                    className="input"
                    placeholder={t.gsr.placeholderQty}
                    aria-label={t.gsr.ariaQty}
                    value={row.qtyRequested ?? ''}
                    onChange={(e) => updateRow(row._key, { qtyRequested: e.target.value === '' ? undefined : Number(e.target.value) })}
                  />
                  <input
                    required
                    className="input"
                    placeholder={t.gsr.placeholderUnit}
                    aria-label={t.gsr.ariaUnit}
                    value={row.unit}
                    onChange={(e) => updateRow(row._key, { unit: e.target.value })}
                  />
                  <input
                    type="text"
                    className="input"
                    placeholder={t.gsr.placeholderRequiredDate}
                    aria-label={t.gsr.ariaRequiredDate}
                    value={row.requiredDate ?? ''}
                    // Show a text placeholder while empty; switch to the native
                    // date picker on focus, and back to text when left blank.
                    onFocus={(e) => { e.target.type = 'date'; }}
                    onBlur={(e) => { if (!e.target.value) e.target.type = 'text'; }}
                    onChange={(e) => updateRow(row._key, { requiredDate: e.target.value || undefined })}
                  />
                  <input
                    className="input"
                    placeholder={t.gsr.placeholderRemarks}
                    aria-label={t.gsr.ariaRemarks}
                    value={row.remarks ?? ''}
                    onChange={(e) => updateRow(row._key, { remarks: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() => removeRow(row._key)}
                    disabled={items.length === 1}
                    className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:pointer-events-none disabled:opacity-30"
                    aria-label={t.gsr.ariaRemove}
                  >
                    <Icon name="delete" size={15} />
                  </button>
                </div>
              ))}
            </div>

            {/* Add row */}
            <button
              type="button"
              onClick={addRow}
              className="flex w-full items-center justify-center gap-1.5 border-t border-dashed border-border py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/40 hover:text-primary"
            >
              <Icon name="add" size={15} /> {t.gsr.addRow}
            </button>
          </div>
        </section>

        {/* Sign-off — requested-by and approver, placed at the foot of the form. */}
        <section className="space-y-2">
          <h2 className="text-base font-semibold text-ink">{t.gsr.signoffSectionTitle}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="card-paper space-y-1 p-4">
              <label className="field-label" htmlFor="requestedBy">{t.gsr.requestedByLabel}</label>
              <select
                id="requestedBy"
                className="input"
                value={requestedById}
                onChange={(e) => setRequestedById(e.target.value)}
              >
                {userOptions.map((u) => (
                  <option key={u.id} value={u.id}>{u.nameDv || u.name}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">{t.gsr.requestedByHint}</p>
            </div>

            <div className="card-paper space-y-1 p-4">
              <label className="field-label" htmlFor="approvedBy">{t.gsr.approvedByLabel}</label>
              <select
                id="approvedBy"
                required
                className="input"
                value={approvedById}
                onChange={(e) => setApprovedById(e.target.value)}
              >
                <option value="" disabled>{t.gsr.selectPerson}</option>
                {userOptions.map((u) => (
                  <option key={u.id} value={u.id}>{u.nameDv || u.name}</option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {submitError && (
          <p className="text-sm text-destructive" role="alert">{submitError}</p>
        )}

        <div className="flex gap-2">
          <button type="submit" disabled={isPending} className="btn-primary">
            {isPending ? t.gsr.submitting : t.gsr.submit}
          </button>
          <button
            type="button"
            onClick={() => void navigate({ to: '/gsr' })}
            className="btn-ghost"
          >
            {t.gsr.cancel}
          </button>
        </div>
      </form>
    </div>
  );
}
